import * as THREE from 'three';
import {
  createLavaMaterial,
  createLunarCraterGeometry,
  createLunarSurfacePatchGeometry,
  LUNAR_ASSET_COLORS,
  lunarCraterProfile,
} from '../assets/LunarAssetKit';
import { MOON_VIEW } from '../scenes/moonComposition';

export type MoonMode = 'character' | 'smooth' | 'impacts' | 'lava' | 'cratered';

const BODY_RADIUS = MOON_VIEW.storyRadius;
const FRONT = new THREE.Vector3(0, 0, 1);
const LAVA_FLOW_DURATION = 1.35;
const LAVA_COOLING_DURATION = 1.1;
const LAVA_SPILL_START = 0.44;
const LAVA_RIM_LEVEL = 0.022;
const HOT_LAVA_COLOR = new THREE.Color(LUNAR_ASSET_COLORS.hotLava);
const COOL_LAVA_COLOR = new THREE.Color(LUNAR_ASSET_COLORS.coolLava);
const HOT_LAVA_EMISSIVE = new THREE.Color(LUNAR_ASSET_COLORS.hotLavaEmissive);
const COOL_LAVA_EMISSIVE = new THREE.Color(LUNAR_ASSET_COLORS.coolLavaEmissive);
export const LAVA_SOURCE_INDICES = [0, 2, 3] as const;
const LAVA_SOURCE_SET = new Set<number>(LAVA_SOURCE_INDICES);

const CRATER_NORMALS = [
  // A few medium landmarks, followed by many smaller impacts. The uneven
  // distribution is intentional: the Moon is not covered in equal circles.
  new THREE.Vector3(-0.54, 0.46, 0.70).normalize(),
  new THREE.Vector3(-0.18, 0.62, 0.76).normalize(),
  new THREE.Vector3(0.30, 0.48, 0.82).normalize(),
  new THREE.Vector3(0.62, 0.24, 0.74).normalize(),
  new THREE.Vector3(-0.68, 0.08, 0.73).normalize(),
  new THREE.Vector3(-0.26, 0.12, 0.96).normalize(),
  new THREE.Vector3(0.18, 0.02, 0.98).normalize(),
  new THREE.Vector3(0.48, -0.22, 0.85).normalize(),
  new THREE.Vector3(-0.54, -0.36, 0.76).normalize(),
  new THREE.Vector3(-0.08, -0.56, 0.82).normalize(),
  new THREE.Vector3(0.30, -0.62, 0.72).normalize(),
  new THREE.Vector3(0.74, -0.44, 0.52).normalize(),
  // A second, smaller set is reserved for the later meteor wave. Keeping
  // these targets separate makes the passage of time read as new impacts,
  // instead of making the same scars flash twice.
  new THREE.Vector3(-0.84, 0.44, 0.42).normalize(),
  new THREE.Vector3(0.82, 0.48, 0.40).normalize(),
  new THREE.Vector3(-0.90, -0.16, 0.34).normalize(),
  new THREE.Vector3(0.88, -0.24, 0.34).normalize(),
  new THREE.Vector3(-0.70, -0.72, 0.28).normalize(),
  new THREE.Vector3(0.06, 0.82, 0.56).normalize(),
  new THREE.Vector3(-0.10, -0.84, 0.50).normalize(),
  new THREE.Vector3(0.68, 0.02, 0.72).normalize(),
];

const CRATER_RADII = [
  0.27, 0.2, 0.17, 0.14, 0.12, 0.1, 0.09, 0.14, 0.11, 0.085, 0.1, 0.075,
  0.068, 0.062, 0.056, 0.052, 0.048, 0.045, 0.042, 0.04,
];
const CRATER_DEPTHS = [
  0.065, 0.05, 0.043, 0.035, 0.03, 0.026, 0.023, 0.035, 0.028, 0.021, 0.025, 0.018,
  0.017, 0.016, 0.015, 0.014, 0.013, 0.012, 0.011, 0.01,
];
export const FORMATION_CRATER_COUNT = 12;
export const STORY_CRATER_COUNT = CRATER_NORMALS.length;

type CraterSlot = {
  group: THREE.Group;
  basin: THREE.Mesh;
  lava: THREE.Mesh;
};

type MariaPatchSlot = {
  sourceIndex: number;
  flow: THREE.Mesh;
  delay: number;
};

const MARIA_PATCH_CONFIGS = [
  { sourceIndex: 0, radius: 0.46, seed: 31, delay: 0.34 },
  { sourceIndex: 2, radius: 0.34, seed: 37, delay: 0.44 },
  { sourceIndex: 3, radius: 0.4, seed: 47, delay: 0.54 },
] as const;

export function getCraterTarget(index: number, pitch: number = MOON_VIEW.pitch, yaw: number = MOON_VIEW.yaw): THREE.Vector3 {
  return CRATER_NORMALS[index % CRATER_NORMALS.length]
    .clone()
    .multiplyScalar(BODY_RADIUS + 0.02)
    .applyEuler(new THREE.Euler(pitch, yaw, 0));
}

export class Moon {
  readonly group = new THREE.Group();

  private readonly bodyGeometry: THREE.SphereGeometry;
  private readonly bodyBasePositions: Float32Array;
  private readonly bodyMaterial = new THREE.MeshStandardMaterial({
    color: LUNAR_ASSET_COLORS.surface,
    roughness: 0.98,
    metalness: 0,
  });
  private readonly body: THREE.Mesh;
  private readonly surfaceTexture: THREE.CanvasTexture;
  private surfaceTextureCanvas: HTMLCanvasElement | null = null;
  private surfaceBasePixels: Uint8ClampedArray | null = null;
  private readonly characterLayer = new THREE.Group();
  private readonly craterLayer = new THREE.Group();
  private readonly mariaLayer = new THREE.Group();
  private readonly craterSlots: CraterSlot[] = [];
  private readonly mariaPatches: MariaPatchSlot[] = [];
  private readonly craterBasinMaterial = new THREE.MeshStandardMaterial({
    color: LUNAR_ASSET_COLORS.crater,
    roughness: 1,
    metalness: 0,
    vertexColors: true,
  });
  private readonly lavaMaterial = createLavaMaterial();
  private readonly characterMaterial = new THREE.MeshStandardMaterial({
    color: '#f4c96a',
    roughness: 0.7,
    metalness: 0.02,
  });
  private readonly faceMaterial = new THREE.MeshStandardMaterial({
    color: '#26354a',
    roughness: 0.7,
    metalness: 0,
  });
  private readonly blushMaterial = new THREE.MeshStandardMaterial({
    color: '#ed8e7c',
    roughness: 0.72,
    metalness: 0,
  });
  private readonly armGeometry = new THREE.CapsuleGeometry(0.13, 0.5, 4, 6);
  private readonly legGeometry = new THREE.CapsuleGeometry(0.15, 0.48, 4, 6);
  private readonly shoeGeometry = new THREE.BoxGeometry(0.3, 0.18, 0.44);
  private readonly eyeGeometry = new THREE.SphereGeometry(0.14, 6, 4);
  private readonly blushGeometry = new THREE.SphereGeometry(0.13, 6, 4);
  private readonly mouthGeometry = new THREE.SphereGeometry(0.18, 8, 5);
  private mode: MoonMode = 'character';
  private craterCount = 0;
  private impactFlash = 0;
  private lavaElapsed = 0;
  private lavaFlowProgress = 0;
  private lavaCoolingProgress = 0;
  private storyYaw: number = MOON_VIEW.yaw;
  private storyPitch: number = MOON_VIEW.pitch;
  private lastMariaTextureProgress = -1;

  constructor() {
    this.surfaceTexture = this.createSurfaceTexture();
    // Formation lava cross-fades into the baked maria texture during cooling.
    // This material instance belongs only to the story Moon, so changing its
    // transparency does not affect the sculpting game's lava.
    this.lavaMaterial.transparent = true;
    this.lavaMaterial.opacity = 1;
    this.bodyGeometry = this.createMoonGeometry();
    this.bodyBasePositions = new Float32Array(
      (this.bodyGeometry.getAttribute('position') as THREE.BufferAttribute).array as ArrayLike<number>,
    );
    this.body = new THREE.Mesh(this.bodyGeometry, this.bodyMaterial);
    this.body.castShadow = true;
    this.body.receiveShadow = true;
    this.group.add(this.body);

    this.createCharacter();
    this.createCraters();
    this.createMariaPatches();

    this.group.add(this.characterLayer, this.craterLayer, this.mariaLayer);
    this.setMode('character');
  }

  update(delta: number, elapsed: number, animate: boolean): void {
    const time = animate ? elapsed : 0;
    const isCharacter = this.mode === 'character';
    this.group.rotation.y = isCharacter ? Math.sin(time * 0.48) * 0.12 : this.storyYaw;
    this.group.rotation.x = isCharacter ? Math.sin(time * 0.6) * 0.025 : this.storyPitch;
    this.group.position.y = isCharacter ? Math.sin(time * 2.1) * 0.11 : 0;

    this.impactFlash = Math.max(0, this.impactFlash - delta * 4.4);
    if (this.mode === 'lava') {
      this.lavaElapsed = Math.min(this.lavaElapsed + delta, LAVA_FLOW_DURATION + LAVA_COOLING_DURATION);
      this.lavaFlowProgress = THREE.MathUtils.clamp(this.lavaElapsed / LAVA_FLOW_DURATION, 0, 1);
      this.lavaCoolingProgress = THREE.MathUtils.clamp(
        (this.lavaElapsed - LAVA_FLOW_DURATION) / LAVA_COOLING_DURATION,
        0,
        1,
      );
    } else if (this.mode === 'cratered') {
      this.lavaElapsed = LAVA_FLOW_DURATION + LAVA_COOLING_DURATION;
      this.lavaFlowProgress = 1;
      this.lavaCoolingProgress = 1;
    }

    this.bodyMaterial.emissive.set(this.impactFlash > 0 ? '#e85a2b' : '#000000');
    this.bodyMaterial.emissiveIntensity = this.impactFlash * 0.45;
    this.updateLavaMaterials(time);

    const showsCoolingLava = this.mode === 'lava' && this.getLavaCompositeProgress() < 1;
    for (const [index, slot] of this.craterSlots.entries()) {
      const cooledIntoMare = LAVA_SOURCE_SET.has(index)
        && (this.mode === 'cratered' || (this.mode === 'lava' && this.lavaCoolingProgress > 0.55));
      slot.basin.visible = !cooledIntoMare;
      // The animated lava is only a temporary layer. Once cooling has fully
      // baked the maria into the Moon texture, leave that texture unobscured.
      slot.lava.visible = showsCoolingLava && LAVA_SOURCE_SET.has(index) && index < this.craterCount;
      if (slot.lava.visible) {
        const filling = THREE.MathUtils.smoothstep(this.lavaFlowProgress, 0, LAVA_SPILL_START);
        const spilling = THREE.MathUtils.smoothstep(this.lavaFlowProgress, LAVA_SPILL_START, 1);
        const fill = this.lavaFlowProgress < LAVA_SPILL_START
          ? THREE.MathUtils.lerp(0.04, 0.58, filling)
          : THREE.MathUtils.lerp(0.58, 1, spilling);
        slot.lava.scale.setScalar(fill);
        slot.lava.position.z = this.lavaFlowProgress < LAVA_SPILL_START
          ? THREE.MathUtils.lerp(-slot.group.userData.depth * 0.82, LAVA_RIM_LEVEL, filling)
          : LAVA_RIM_LEVEL;
      }
    }
    this.updateMariaPatches();
    this.updateMariaTexture();
  }

  setMode(mode: MoonMode): void {
    this.mode = mode;
    const isCharacter = mode === 'character';
    const isStoryMoon = !isCharacter;
    const showsMaria = mode === 'lava' || mode === 'cratered';

    this.characterLayer.visible = isCharacter;
    this.craterLayer.visible = isStoryMoon;
    this.mariaLayer.visible = showsMaria;
    this.surfaceTexture.needsUpdate = true;

    if (isCharacter) {
      this.bodyMaterial.color.set('#f4c96a');
      this.bodyMaterial.map = null;
      this.bodyMaterial.bumpMap = null;
      this.bodyMaterial.bumpScale = 0;
      this.bodyMaterial.roughness = 0.7;
      this.setCraterCount(0);
      this.lavaElapsed = 0;
      this.lavaFlowProgress = 0;
      this.lavaCoolingProgress = 0;
      this.setLavaVisibility(false);
      this.updateLavaMaterials(0);
      this.rebuildMariaTexture(0, true);
      this.bodyMaterial.needsUpdate = true;
      return;
    }

    this.bodyMaterial.color.set(LUNAR_ASSET_COLORS.surface);
    this.bodyMaterial.map = this.surfaceTexture;
    this.bodyMaterial.bumpMap = this.surfaceTexture;
    this.bodyMaterial.bumpScale = 0.022;
    this.bodyMaterial.roughness = 0.94;
    this.bodyMaterial.needsUpdate = true;
    this.lavaElapsed = mode === 'cratered' ? LAVA_FLOW_DURATION + LAVA_COOLING_DURATION : 0;
    this.lavaFlowProgress = mode === 'cratered' ? 1 : 0;
    this.lavaCoolingProgress = mode === 'cratered' ? 1 : 0;
    this.setLavaVisibility(mode === 'lava');
    this.updateLavaMaterials(0);

    if (mode === 'smooth') this.setCraterCount(0);
    if (mode === 'impacts') this.setCraterCount(Math.min(this.craterCount, 1));
    if (mode === 'lava') this.setCraterCount(Math.max(this.craterCount, 3));
    this.rebuildMariaTexture(mode === 'cratered' ? 1 : 0, true);
  }

  revealNextCrater(): number {
    this.craterCount = Math.min(this.craterSlots.length, this.craterCount + 1);
    this.setCraterCount(this.craterCount);
    this.impactFlash = 1;
    return this.craterCount;
  }

  setCraterCount(count: number): void {
    const previousMariaSourceCount = MARIA_PATCH_CONFIGS.filter(
      (patch) => patch.sourceIndex < this.craterCount,
    ).length;
    this.craterCount = THREE.MathUtils.clamp(Math.floor(count), 0, this.craterSlots.length);
    this.craterSlots.forEach((slot, index) => {
      slot.group.visible = index < this.craterCount;
      slot.lava.visible = this.mode === 'lava' && LAVA_SOURCE_SET.has(index) && index < this.craterCount;
    });
    this.rebuildCrateredSurface();
    const nextMariaSourceCount = MARIA_PATCH_CONFIGS.filter(
      (patch) => patch.sourceIndex < this.craterCount,
    ).length;
    if (nextMariaSourceCount !== previousMariaSourceCount) {
      const progress = this.mode === 'cratered'
        ? 1
        : this.mode === 'lava'
          ? THREE.MathUtils.smoothstep(this.lavaCoolingProgress, 0, 1)
          : 0;
      this.rebuildMariaTexture(progress, true);
    }
  }

  getMode(): MoonMode {
    return this.mode;
  }

  getCraterCount(): number {
    return this.craterCount;
  }

  getLavaFlowProgress(): number {
    return this.lavaFlowProgress;
  }

  getLavaCoolingProgress(): number {
    return this.lavaCoolingProgress;
  }

  getLavaSourceCount(): number {
    return LAVA_SOURCE_INDICES.length;
  }

  getActiveLavaSourceCount(): number {
    return this.craterSlots.reduce((count, slot, index) => count + (slot.lava.visible && LAVA_SOURCE_SET.has(index) ? 1 : 0), 0);
  }

  getActiveLavaCraterCount(): number {
    return this.craterSlots.reduce((count, slot) => count + (slot.lava.visible ? 1 : 0), 0);
  }

  dispose(): void {
    this.bodyGeometry.dispose();
    this.bodyMaterial.dispose();
    this.surfaceTexture.dispose();
    this.armGeometry.dispose();
    this.legGeometry.dispose();
    this.shoeGeometry.dispose();
    this.eyeGeometry.dispose();
    this.blushGeometry.dispose();
    this.mouthGeometry.dispose();
    this.characterMaterial.dispose();
    this.faceMaterial.dispose();
    this.blushMaterial.dispose();
    this.craterBasinMaterial.dispose();
    this.lavaMaterial.dispose();

    for (const slot of this.craterSlots) {
      slot.basin.geometry.dispose();
      slot.lava.geometry.dispose();
    }
    for (const patch of this.mariaPatches) {
      patch.flow.geometry.dispose();
    }
  }

  private createMoonGeometry(): THREE.SphereGeometry {
    const geometry = new THREE.SphereGeometry(BODY_RADIUS, 48, 32);
    const position = geometry.attributes.position;
    const point = new THREE.Vector3();

    for (let index = 0; index < position.count; index += 1) {
      point.fromBufferAttribute(position, index);
      const noise =
        Math.sin(point.x * 4.7 + point.y * 1.9) * 0.45 +
        Math.sin(point.y * 7.1 - point.z * 3.2) * 0.3 +
        Math.sin(point.z * 10.4 + point.x * 2.6) * 0.25;
      point.normalize().multiplyScalar(BODY_RADIUS * (1 + noise * 0.0038));
      position.setXYZ(index, point.x, point.y, point.z);
    }

    geometry.computeVertexNormals();
    return geometry;
  }

  private createSurfaceTexture(): THREE.CanvasTexture {
    const width = 192;
    const height = 96;
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const context = canvas.getContext('2d');
    if (!context) throw new Error('Could not create moon surface texture context.');

    const image = context.createImageData(width, height);
    for (let y = 0; y < height; y += 1) {
      for (let x = 0; x < width; x += 1) {
        const wave =
          Math.sin(x * 0.17 + y * 0.08) * 8 +
          Math.sin(x * 0.043 - y * 0.19) * 5 +
          Math.sin((x + y) * 0.63) * 2;
        const value = THREE.MathUtils.clamp(Math.round(184 + wave), 156, 212);
        const offset = (y * width + x) * 4;
        image.data[offset] = value - 2;
        image.data[offset + 1] = value;
        image.data[offset + 2] = value + 3;
        image.data[offset + 3] = 255;
      }
    }
    context.putImageData(image, 0, 0);
    this.surfaceTextureCanvas = canvas;
    this.surfaceBasePixels = new Uint8ClampedArray(image.data);

    const texture = new THREE.CanvasTexture(canvas);
    texture.colorSpace = THREE.SRGBColorSpace;
    texture.wrapS = THREE.RepeatWrapping;
    texture.wrapT = THREE.ClampToEdgeWrapping;
    texture.minFilter = THREE.LinearMipmapLinearFilter;
    texture.magFilter = THREE.LinearFilter;
    return texture;
  }

  private createCharacter(): void {
    const armLeft = new THREE.Mesh(this.armGeometry, this.characterMaterial);
    armLeft.position.set(-1.84, 0.05, 0.06);
    armLeft.rotation.z = -0.82;
    armLeft.castShadow = true;

    const armRight = armLeft.clone();
    armRight.position.x = 1.84;
    armRight.rotation.z = 0.82;

    const legLeft = new THREE.Mesh(this.legGeometry, this.characterMaterial);
    legLeft.position.set(-0.62, -2.03, 0.04);
    legLeft.rotation.z = -0.08;
    legLeft.castShadow = true;

    const legRight = legLeft.clone();
    legRight.position.x = 0.62;
    legRight.rotation.z = 0.08;

    const shoeLeft = new THREE.Mesh(this.shoeGeometry, this.faceMaterial);
    shoeLeft.position.set(-0.69, -2.36, 0.12);
    shoeLeft.rotation.y = -0.1;
    const shoeRight = shoeLeft.clone();
    shoeRight.position.x = 0.69;
    shoeRight.rotation.y = 0.1;

    this.characterLayer.add(armLeft, armRight, legLeft, legRight, shoeLeft, shoeRight);

    const eyeLeft = new THREE.Mesh(this.eyeGeometry, this.faceMaterial);
    eyeLeft.position.set(-0.49, 0.42, 1.87);
    const eyeRight = eyeLeft.clone();
    eyeRight.position.x = 0.49;

    const cheekLeft = new THREE.Mesh(this.blushGeometry, this.blushMaterial);
    cheekLeft.position.set(-0.78, -0.04, 1.82);
    const cheekRight = cheekLeft.clone();
    cheekRight.position.x = 0.78;

    const mouth = new THREE.Mesh(this.mouthGeometry, this.faceMaterial);
    mouth.position.set(0, -0.16, 1.9);
    mouth.scale.set(1, 0.78, 0.28);

    this.characterLayer.add(eyeLeft, eyeRight, cheekLeft, cheekRight, mouth);
  }

  private createCraters(): void {
    CRATER_NORMALS.forEach((normal, index) => {
      const radius = CRATER_RADII[index];
      const depth = CRATER_DEPTHS[index];
      const group = new THREE.Group();
      // Keep the bowl close to the surface. Raising it by the full depth makes
      // it read like a cup or a submarine window instead of a shallow crater.
      group.position.copy(normal).multiplyScalar(BODY_RADIUS);
      group.quaternion.setFromUnitVectors(FRONT, normal);
      group.userData.depth = depth;

      const basin = new THREE.Mesh(
        createLunarCraterGeometry({ radius, depth, moonRadius: BODY_RADIUS, seed: index }),
        this.craterBasinMaterial,
      );
      basin.castShadow = true;

      const lava = new THREE.Mesh(
        createLunarSurfacePatchGeometry({
          radius: radius * 1.72,
          moonRadius: BODY_RADIUS,
          seed: index + 17,
        }),
        this.lavaMaterial,
      );
      lava.position.z = -depth * 0.82;
      lava.scale.setScalar(0.04);
      lava.visible = false;

      group.add(basin, lava);
      this.craterLayer.add(group);
      this.craterSlots.push({ group, basin, lava });
    });
  }

  private createMariaPatches(): void {
    for (const patch of MARIA_PATCH_CONFIGS) {
      const normal = CRATER_NORMALS[patch.sourceIndex];
      const group = new THREE.Group();
      group.position.copy(normal).multiplyScalar(BODY_RADIUS);
      group.quaternion.setFromUnitVectors(FRONT, normal);

      const flow = new THREE.Mesh(
        createLunarSurfacePatchGeometry({
          radius: patch.radius * 0.925,
          moonRadius: BODY_RADIUS,
          seed: patch.seed + 71,
          surfaceOffset: 0.02,
        }),
        this.lavaMaterial,
      );
      flow.visible = false;

      group.add(flow);
      this.mariaLayer.add(group);
      this.mariaPatches.push({ sourceIndex: patch.sourceIndex, flow, delay: patch.delay });
    }
  }

  private rebuildCrateredSurface(): void {
    const position = this.bodyGeometry.getAttribute('position') as THREE.BufferAttribute;
    const point = new THREE.Vector3();
    const normal = new THREE.Vector3();

    for (let index = 0; index < position.count; index += 1) {
      point.fromArray(this.bodyBasePositions, index * 3);
      normal.copy(point).normalize();
      let radialOffset = 0;

      for (let craterIndex = 0; craterIndex < this.craterCount; craterIndex += 1) {
        const dot = THREE.MathUtils.clamp(normal.dot(CRATER_NORMALS[craterIndex]), -1, 1);
        const surfaceDistance = Math.sqrt(Math.max(0, 2 - 2 * dot)) * BODY_RADIUS;
        const radius = CRATER_RADII[craterIndex];
        if (surfaceDistance >= radius) continue;
        radialOffset += lunarCraterProfile(surfaceDistance / radius, CRATER_DEPTHS[craterIndex]);
      }

      const baseRadius = point.length();
      position.setXYZ(
        index,
        normal.x * (baseRadius + radialOffset),
        normal.y * (baseRadius + radialOffset),
        normal.z * (baseRadius + radialOffset),
      );
    }

    position.needsUpdate = true;
    this.bodyGeometry.computeVertexNormals();
    this.bodyGeometry.computeBoundingSphere();
  }

  private setLavaVisibility(visible: boolean): void {
    this.craterSlots.forEach((slot, index) => {
      slot.lava.visible = visible && LAVA_SOURCE_SET.has(index) && index < this.craterCount;
    });
  }

  private updateMariaPatches(): void {
    const isLava = this.mode === 'lava';
    const isCratered = this.mode === 'cratered';
    const showsCoolingLava = isLava && this.getLavaCompositeProgress() < 1;
    const flowProgress = isLava || isCratered ? this.lavaFlowProgress : 0;

    for (const patch of this.mariaPatches) {
      const sourceVisible = this.craterCount > patch.sourceIndex;
      const localProgress = THREE.MathUtils.clamp(
        (flowProgress - patch.delay) / (1 - patch.delay),
        0,
        1,
      );
      const spread = THREE.MathUtils.smoothstep(localProgress, 0, 1);
      const scale = 0.08 + spread * 0.92;

      patch.flow.visible = showsCoolingLava && sourceVisible && localProgress > 0.005;
      patch.flow.scale.setScalar(scale);
    }
  }

  private updateMariaTexture(): void {
    const progress = this.mode === 'cratered'
      ? 1
      : this.mode === 'lava'
        ? this.getLavaCompositeProgress()
        : 0;
    this.rebuildMariaTexture(progress);
  }

  private getLavaCompositeProgress(): number {
    // Cooling does not begin until lavaFlowProgress reaches 1, so this one
    // curve guarantees: fill first, then fade lava and reveal maria together.
    return THREE.MathUtils.smootherstep(this.lavaCoolingProgress, 0, 1);
  }

  /**
   * Bake cooled maria into the Moon texture using the same coastline,
   * mottling, target RGB, and opacity model as the sculpting game.
   */
  private rebuildMariaTexture(progress: number, force = false): void {
    if (!this.surfaceTextureCanvas || !this.surfaceBasePixels) return;
    const normalizedProgress = THREE.MathUtils.clamp(progress, 0, 1);
    if (!force && Math.abs(normalizedProgress - this.lastMariaTextureProgress) < 0.025) return;
    this.lastMariaTextureProgress = normalizedProgress;

    const context = this.surfaceTextureCanvas.getContext('2d');
    if (!context) return;
    const width = this.surfaceTextureCanvas.width;
    const height = this.surfaceTextureCanvas.height;
    const image = context.createImageData(width, height);
    image.data.set(this.surfaceBasePixels);
    const mares = MARIA_PATCH_CONFIGS
      .filter((patch) => patch.sourceIndex < this.craterCount)
      .map((patch) => {
        const normal = CRATER_NORMALS[patch.sourceIndex];
        const reference = Math.abs(normal.y) < 0.9
          ? new THREE.Vector3(0, 1, 0)
          : new THREE.Vector3(1, 0, 0);
        const tangentX = reference.cross(normal).normalize();
        const tangentY = new THREE.Vector3().crossVectors(normal, tangentX).normalize();
        return { patch, normal, tangentX, tangentY };
      });

    if (normalizedProgress > 0 && mares.length > 0) {
      for (let y = 0; y < height; y += 1) {
        const phi = (y / (height - 1)) * Math.PI;
        const sinPhi = Math.sin(phi);
        const ny = Math.cos(phi);
        for (let x = 0; x < width; x += 1) {
          const theta = (x / width) * Math.PI * 2;
          const nx = -Math.cos(theta) * sinPhi;
          const nz = Math.sin(theta) * sinPhi;
          const offset = (y * width + x) * 4;
          let red = image.data[offset];
          let green = image.data[offset + 1];
          let blue = image.data[offset + 2];

          for (const { patch, normal, tangentX, tangentY } of mares) {
            const dot = THREE.MathUtils.clamp(nx * normal.x + ny * normal.y + nz * normal.z, -1, 1);
            const surfaceDistance = Math.sqrt(Math.max(0, 2 - 2 * dot)) * BODY_RADIUS;
            const localX = nx * tangentX.x + ny * tangentX.y + nz * tangentX.z;
            const localY = nx * tangentY.x + ny * tangentY.y + nz * tangentY.z;
            const angle = Math.atan2(localY, localX);
            const coastVariation =
              Math.sin(angle * 3 + patch.seed * 0.83) * 0.085
              + Math.sin(angle * 5 - patch.seed * 0.47) * 0.045
              + Math.sin(angle * 9 + patch.seed * 1.19) * 0.022;
            const mareRadius = patch.radius * (1 + coastVariation);
            if (surfaceDistance >= mareRadius) continue;
            const coverage = 1 - THREE.MathUtils.smoothstep(surfaceDistance / mareRadius, 0.7, 1);
            const mottling =
              Math.sin(nx * 43 + ny * 61 + nz * 37 + patch.seed) * 3.5
              + Math.sin(nx * 97 - ny * 53 + nz * 71 - patch.seed * 0.7) * 2;
            const opacity = coverage * 0.86 * normalizedProgress;
            red = THREE.MathUtils.lerp(red, 94 + mottling, opacity);
            green = THREE.MathUtils.lerp(green, 95 + mottling * 0.92, opacity);
            blue = THREE.MathUtils.lerp(blue, 92 + mottling * 0.78, opacity);
          }

          image.data[offset] = Math.round(red);
          image.data[offset + 1] = Math.round(green);
          image.data[offset + 2] = Math.round(blue);
        }
      }
    }

    context.putImageData(image, 0, 0);
    this.surfaceTexture.needsUpdate = true;
  }

  private updateLavaMaterials(time: number): void {
    const cooling = this.getLavaCompositeProgress();
    this.lavaMaterial.color.lerpColors(HOT_LAVA_COLOR, COOL_LAVA_COLOR, cooling);
    this.lavaMaterial.emissive.lerpColors(HOT_LAVA_EMISSIVE, COOL_LAVA_EMISSIVE, cooling);
    this.lavaMaterial.opacity = this.mode === 'lava' ? 1 - cooling : 0;
    this.lavaMaterial.emissiveIntensity =
      this.mode === 'lava'
        ? THREE.MathUtils.lerp(1.8, 0.08, cooling) + Math.sin(time * 6.5) * 0.18 * (1 - cooling)
        : 0;
  }
}
